import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const usePWAStore = defineStore('pwa', () => {
  // State
  const isOnline = ref(navigator.onLine)
  const needRefresh = ref(false)
  const offlineReady = ref(false)
  const showInstallPrompt = ref(false)
  const deferredPrompt = ref(null)
  const installDismissed = ref(false)
  const updateServiceWorker = ref(null)
  const lastOnlineTime = ref(Date.now())
  const reconnected = ref(false)

  // Computed
  const isOffline = computed(() => !isOnline.value)
  const canInstall = computed(() => !!deferredPrompt.value && !installDismissed.value)
  const hasUpdate = computed(() => needRefresh.value)

  // Actions
  function setOnline(value) {
    const wasOffline = !isOnline.value
    isOnline.value = value

    if (value && wasOffline) {
      // User just came back online
      reconnected.value = true
      lastOnlineTime.value = Date.now()

      // Auto-hide reconnected message after 3 seconds
      setTimeout(() => {
        reconnected.value = false
      }, 3000)
    }
  }

  function setNeedRefresh(value) {
    needRefresh.value = value
  }

  function setOfflineReady(value) {
    offlineReady.value = value
  }

  function setUpdateServiceWorker(fn) {
    updateServiceWorker.value = fn
  }

  function setDeferredPrompt(event) {
    deferredPrompt.value = event
    // Check if previously dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10)
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24)
      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        installDismissed.value = true
        return
      }
    }
    showInstallPrompt.value = true
  }

  async function installApp() {
    if (!deferredPrompt.value) return false

    try {
      deferredPrompt.value.prompt()
      const { outcome } = await deferredPrompt.value.userChoice

      // Always hide prompt after user choice
      showInstallPrompt.value = false
      deferredPrompt.value = null

      if (outcome === 'accepted') {
        console.log('PWA installed successfully')
        return true
      } else {
        console.log('PWA installation declined')
        return false
      }
    } catch (error) {
      console.error('PWA installation error:', error)
      return false
    }
  }

  function dismissInstallPrompt() {
    showInstallPrompt.value = false
    installDismissed.value = true
    localStorage.setItem('pwa-install-dismissed', Date.now().toString())
  }

  function clearInstallDismissal() {
    localStorage.removeItem('pwa-install-dismissed')
    installDismissed.value = false
    if (deferredPrompt.value) {
      showInstallPrompt.value = true
    }
  }

  async function updateApp() {
    if (updateServiceWorker.value) {
      await updateServiceWorker.value(true)
      needRefresh.value = false
    }
  }

  function dismissUpdate() {
    needRefresh.value = false
  }

  function initNetworkListeners() {
    window.addEventListener('online', () => setOnline(true))
    window.addEventListener('offline', () => setOnline(false))
  }

  function initInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
    })

    window.addEventListener('appinstalled', () => {
      console.log('PWA was installed')
      deferredPrompt.value = null
      showInstallPrompt.value = false
    })
  }

  function initialize() {
    initNetworkListeners()
    initInstallPrompt()
  }

  return {
    // State
    isOnline,
    needRefresh,
    offlineReady,
    showInstallPrompt,
    deferredPrompt,
    installDismissed,
    lastOnlineTime,
    reconnected,

    // Computed
    isOffline,
    canInstall,
    hasUpdate,

    // Actions
    setOnline,
    setNeedRefresh,
    setOfflineReady,
    setUpdateServiceWorker,
    setDeferredPrompt,
    installApp,
    dismissInstallPrompt,
    clearInstallDismissal,
    updateApp,
    dismissUpdate,
    initialize
  }
})
